import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { categoryService } from '../services/categoryService';
import toast from 'react-hot-toast';

export const useCategories = (params = {}) => {
  return useQuery(
    ['categories', params],
    () => categoryService.getCategories(params),
    {
      staleTime: 300000 // 5 minutes
    }
  );
};

export const useCategoriesWithSubcategories = (moduleId = null) => {
  return useQuery(
    ['categories-with-subcategories', moduleId],
    () => categoryService.getCategoriesWithSubcategories(moduleId),
    {
      staleTime: 300000
    }
  );
};

export const useCategoriesByModule = (moduleId) => {
  return useQuery(
    ['categories-by-module', moduleId],
    () => categoryService.getCategoriesByModule(moduleId),
    {
      enabled: !!moduleId,
      staleTime: 300000
    }
  );
};

export const useCategory = (categoryId) => {
  return useQuery(
    ['category', categoryId],
    () => categoryService.getCategory(categoryId),
    {
      enabled: !!categoryId,
      staleTime: 300000
    }
  );
};

export const useCategoryWithSubcategories = (categoryId) => {
  return useQuery(
    ['category-with-subcategories', categoryId],
    () => categoryService.getCategoryWithSubcategories(categoryId),
    {
      enabled: !!categoryId,
      staleTime: 300000
    }
  );
};

export const useCreateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation(categoryService.createCategory, {
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
      queryClient.invalidateQueries(['categories-with-subcategories']);
      toast.success('Category created successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create category');
    }
  });
};

export const useUpdateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ categoryId, data }) => categoryService.updateCategory(categoryId, data),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['category', variables.categoryId]);
        queryClient.invalidateQueries(['categories']);
        queryClient.invalidateQueries(['categories-with-subcategories']);
        toast.success('Category updated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update category');
      }
    }
  );
};

export const useDeleteCategory = () => {
  const queryClient = useQueryClient();

  return useMutation(categoryService.deleteCategory, {
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
      queryClient.invalidateQueries(['categories-with-subcategories']);
      toast.success('Category deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete category');
    }
  });
};

export const useActivateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation(categoryService.activateCategory, {
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
      toast.success('Category activated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to activate category');
    }
  });
};

export const useDeactivateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation(categoryService.deactivateCategory, {
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
      toast.success('Category deactivated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to deactivate category');
    }
  });
};